/**
 * Player-facing client for the venue leaderboard (relay/leaderboard.ts): find the running period,
 * register a player, start a leaderboard game. Also the pure bits — turning a period's frozen rules
 * into game settings, and every error message a player can see.
 *
 * PINs are typed at setup, sent once to the relay and dropped: nothing here stores one.
 */

import { settingsLookRight } from '../game/backup';
import type { LeaderboardInfo } from '../game/types';
import { DEFAULT_SETTINGS, makeSettings, type HkosSettings } from '../rules/hkos';
import { clearSession, getSessionToken, type Coordinates } from './activation';
import { SETTLEMENT_MODE } from './leaderboardAdmin';
import { DEFAULT_SCREEN_SECONDS, type DisplayBoard, type DisplayBranding, type DisplaySettings } from './leaderboardDisplay';
import type { Theme } from './theme';

// Same base-URL pattern as src/ui/activation.ts.
const RELAY_BASE = import.meta.env?.VITE_VISION_RELAY_URL ?? '/api';

export type LeaderboardSettlement = 'zero_sum' | 'winner_only';

/**
 * Whether the relay found this device at the venue (from the location sent with the request). Anything but 'ok'
 * is shown to the player before any setup screen, using the same wording as a refused start.
 */
export type LeaderboardAccess = 'ok' | 'outside_venue' | 'location_needed';

/** The message for a device the relay says is not at the venue, or has not shared where it is. */
export function describeAccess(access: Exclude<LeaderboardAccess, 'ok'>): string {
  return describeStartError(access === 'outside_venue' ? 'not_at_venue' : 'location_required', '');
}

/** A running leaderboard period, as the relay describes it (see relay/leaderboard.ts's `Period`). */
export interface LeaderboardPeriodInfo {
  id: string;
  venue: string;
  startsAt: number;
  endsAt: number;
  minRounds: number;
  settlement: LeaderboardSettlement;
  maxGamesPerDay: number;
  maxGamesPerPeriod: number | null;
  minSecondsPerRound: number;
  /** The frozen house rules: `{ mode, toggles, serious, fun }`. Opaque to the relay; read by `periodRulesToSettings`. */
  rules: unknown;
}

export interface SeatNotCounted {
  nickname: string;
  reason: 'daily_limit' | 'period_limit';
}

export interface StartedLeaderboardGame {
  gameId: string;
  period: LeaderboardPeriodInfo;
  /** The four nicknames as registered (their capitalisation), in the order they were sent. */
  players: string[];
  notCounted: SeatNotCounted[];
}

// --- messages ---------------------------------------------------------------

/**
 * The standard temporary PIN a staff unlock gives an account (mirrors `DEFAULT_PIN` in relay/leaderboard.ts — a test keeps the two
 * equal). The player types it, is taken to choose their own, and it is refused as a new PIN.
 */
export const DEFAULT_PIN = '1234';

export const MSG_NETWORK = 'Could not reach the leaderboard. Check your connection and try again.';
export const MSG_UNAVAILABLE = 'The leaderboard is not available yet.';
export const MSG_REACTIVATE = 'This device needs to be activated again.';

/**
 * What to tell a player when a leaderboard game can't start. Never says how long a lockout lasts (the
 * project's standing rule for security messages), and points an unregistered player at registration.
 */
export function describeStartError(code: string, serverMessage: string, player?: string): string {
  const who = player ?? 'That player';
  switch (code) {
    case 'unknown':
      return `${who} is not registered yet. Tap "New player? Register" first.`;
    case 'wrong_pin':
      return `Wrong PIN for ${who}.`;
    case 'locked':
      return `Too many wrong attempts for ${who}.`;
    case 'disabled':
      return 'Leaderboard is not available currently.';
    case 'pending':
      return `${who} is waiting for staff approval. Ask the staff to approve the request, then try again.`;
    case 'pin_change_required':
      return `${who} needs to choose a new PIN first.`;
    case 'reset_expired':
      return `The PIN reset for ${who} has expired. Ask the staff to unlock the account again.`;
    case 'no_active_period':
      return 'There is no leaderboard running right now.';
    case 'not_at_venue':
      return 'Leaderboard games can only be started at the venue.';
    case 'location_required':
      return 'Allow location access for this site so the venue can be checked, then try again.';
    case 'all_at_limit':
      return 'Nobody at this table has any leaderboard games left, so this game would not count for anyone. You can still play a normal game.';
    default:
      return serverMessage || 'Could not start the leaderboard game.';
  }
}

export function describeNotCounted(seats: SeatNotCounted[]): string[] {
  return seats.map((s) =>
    s.reason === 'daily_limit'
      ? `${s.nickname} has reached today's limit`
      : `${s.nickname} has reached the limit for this whole period`,
  );
}

// --- requests -----------------------------------------------------------------

export type Failure = { ok: false; error: string; code?: string; player?: string };
export type RelayReply = { ok: boolean; status: number; json: Record<string, unknown> };

export async function relayRequest(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  /** Skip the browser's cached copy — for a board read right after this device changed it. */
  fresh = false,
): Promise<RelayReply | Failure> {
  const token = getSessionToken();
  if (!token) return { ok: false, error: MSG_REACTIVATE };
  let res: Response;
  try {
    res = await fetch(`${RELAY_BASE}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(fresh ? { cache: 'no-store' as const } : {}),
    });
  } catch {
    return { ok: false, error: MSG_NETWORK };
  }
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 401) {
    clearSession();
    return { ok: false, error: MSG_REACTIVATE };
  }
  if (res.status === 503) return { ok: false, error: MSG_UNAVAILABLE };
  return { ok: res.ok, status: res.status, json };
}

const isFailure = (r: RelayReply | Failure): r is Failure => !('status' in r);

/**
 * The period running at this venue right now, or `period: null` if none is. Sends the device's location
 * when it has one, so the relay works out the venue from where the device REALLY is.
 */
export async function fetchActivePeriod(
  coords?: Coordinates,
): Promise<{ ok: true; venue: string; access: LeaderboardAccess; period: LeaderboardPeriodInfo | null } | { ok: false; error: string }> {
  const query = coords ? `&latitude=${coords.latitude}&longitude=${coords.longitude}` : '';
  const res = await relayRequest('GET', `/leaderboard/top?limit=1${query}`);
  if (isFailure(res)) return { ok: false, error: res.error };
  if (!res.ok) return { ok: false, error: (res.json.error as string) || MSG_UNAVAILABLE };
  return {
    ok: true,
    venue: String(res.json.venue ?? ''),
    access: res.json.access === 'outside_venue' || res.json.access === 'location_needed' ? res.json.access : 'ok',
    period: (res.json.period as LeaderboardPeriodInfo | null) ?? null,
  };
}

export interface LeaderboardEntry {
  rank: number;
  nickname: string;
  points: number;
  games: number;
  wins: number;
}

/** The venue's board for the running period: the top `limit` players (the relay caps this at 100). */
export async function fetchLeaderboardBoard(
  coords?: Coordinates,
  limit = 50,
): Promise<
  | { ok: true; venue: string; access: LeaderboardAccess; period: LeaderboardPeriodInfo | null; entries: LeaderboardEntry[] }
  | { ok: false; error: string }
> {
  const query = coords ? `&latitude=${coords.latitude}&longitude=${coords.longitude}` : '';
  const res = await relayRequest('GET', `/leaderboard/top?limit=${limit}${query}`, undefined, true);
  if (isFailure(res)) return { ok: false, error: res.error };
  if (!res.ok) return { ok: false, error: (res.json.error as string) || MSG_UNAVAILABLE };
  return {
    ok: true,
    venue: String(res.json.venue ?? ''),
    access: res.json.access === 'outside_venue' || res.json.access === 'location_needed' ? res.json.access : 'ok',
    period: (res.json.period as LeaderboardPeriodInfo | null) ?? null,
    entries: Array.isArray(res.json.entries) ? (res.json.entries as LeaderboardEntry[]) : [],
  };
}

/** The board pages a player's phone shows: the running season, or the last finished one, with its state (see leaderboardDisplay.ts). */
export async function fetchDisplayBoard(
  coords?: Coordinates,
): Promise<
  | { ok: true; venue: string; access: LeaderboardAccess; board: DisplayBoard | null; display: DisplaySettings }
  | { ok: false; error: string }
> {
  const query = coords ? `&latitude=${coords.latitude}&longitude=${coords.longitude}` : '';
  const res = await relayRequest('GET', `/leaderboard/top?display=1&limit=50${query}`, undefined, true);
  if (isFailure(res)) return { ok: false, error: res.error };
  if (!res.ok) return { ok: false, error: (res.json.error as string) || MSG_UNAVAILABLE };
  return {
    ok: true,
    venue: String(res.json.venue ?? ''),
    access: res.json.access === 'outside_venue' || res.json.access === 'location_needed' ? res.json.access : 'ok',
    board: (res.json.board as DisplayBoard | null) ?? null,
    display: readDisplaySettings(res.json.display),
  };
}

/** The settings as the relay sent them, with the shipped defaults for anything missing or odd. */
export function readDisplaySettings(raw: unknown): DisplaySettings {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<DisplaySettings>;
  return {
    screenSeconds: Number.isInteger(r.screenSeconds) && r.screenSeconds! > 0 ? r.screenSeconds! : DEFAULT_SCREEN_SECONDS,
    showTable: r.showTable !== false,
    showPodium: r.showPodium !== false,
    theme: typeof r.theme === 'string' ? (r.theme as Theme) : null,
    logoPlate: r.logoPlate === true,
  };
}

export type TvReply =
  | { ok: true; venue: string; board: DisplayBoard | null; display: DisplaySettings; branding: DisplayBranding | null; generatedAt: number }
  | { ok: false; reason: 'bad_link' | 'unavailable' | 'network' };

/** The board for a TV, read with the TV link's token alone — a TV has no session. `bad_link` = the link was replaced or removed. */
export async function fetchTvBoard(token: string): Promise<TvReply> {
  let res: Response;
  try {
    res = await fetch(`${RELAY_BASE}/leaderboard/tv?token=${encodeURIComponent(token)}`, { cache: 'no-store' });
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (res.status === 404) return { ok: false, reason: 'bad_link' };
  if (!res.ok) return { ok: false, reason: 'unavailable' };
  const json = (await res.json().catch(() => undefined)) as Record<string, unknown> | undefined;
  if (!json) return { ok: false, reason: 'unavailable' };
  return {
    ok: true,
    venue: String(json.venue ?? ''),
    board: (json.board as DisplayBoard | null) ?? null,
    display: readDisplaySettings(json.display),
    branding: (json.branding as DisplayBranding | null) ?? null,
    generatedAt: typeof json.generatedAt === 'number' ? json.generatedAt : Date.now(),
  };
}

/** What a finished game sends to the relay: the round count and each player's final points and rounds won. */
export interface LeaderboardResultPayload {
  gameId: string;
  rounds: number;
  results: { nickname: string; points: number; wins: number }[];
}

/**
 * What became of one submission. Only `sent` is final-good; `retry` means try again later (offline, the relay is
 * busy or down); `reactivate` means this device's session ran out; `rejected` is the relay saying no for a reason
 * a retry cannot fix — the reason is worded for the host.
 */
export type SubmitOutcome =
  | { kind: 'sent'; duplicate: boolean }
  | { kind: 'retry'; reason: string }
  | { kind: 'reactivate' }
  | { kind: 'rejected'; code: string; message: string };

/** Turns the relay's answer to /leaderboard/submit into a `SubmitOutcome` (pure, so it is tested without a network). */
export function classifySubmitReply(status: number, json: Record<string, unknown>): SubmitOutcome {
  if (status >= 200 && status < 300) return { kind: 'sent', duplicate: json.duplicate === true };
  // Busy or broken on the relay's side: the result is fine, so keep it and try again later.
  if (status === 429 || status >= 500) return { kind: 'retry', reason: MSG_NETWORK };
  const code = typeof json.code === 'string' ? json.code : 'invalid';
  const message =
    code === 'unknown_game'
      ? 'The leaderboard no longer recognises this game — one of its players probably started another leaderboard game first.'
      : typeof json.error === 'string' && json.error
        ? json.error
        : 'The leaderboard did not accept this result.';
  return { kind: 'rejected', code, message };
}

export async function submitLeaderboardResult(payload: LeaderboardResultPayload): Promise<SubmitOutcome> {
  const res = await relayRequest('POST', '/leaderboard/submit', payload);
  if (isFailure(res)) return res.error === MSG_REACTIVATE ? { kind: 'reactivate' } : { kind: 'retry', reason: res.error };
  return classifySubmitReply(res.status, res.json);
}

/** 'pending' = the request is with the staff; 'approved' = they can play now (approval is switched off). */
export async function registerLeaderboardPlayer(
  nickname: string,
  pin: string,
): Promise<{ ok: true; nickname: string; status: 'pending' | 'approved' } | { ok: false; error: string }> {
  const res = await relayRequest('POST', '/leaderboard/register', { nickname, pin });
  if (isFailure(res)) return { ok: false, error: res.error };
  if (!res.ok) return { ok: false, error: (res.json.error as string) || 'Could not register that player.' };
  return { ok: true, nickname: String(res.json.nickname ?? nickname), status: res.json.status === 'pending' ? 'pending' : 'approved' };
}

/** Why choosing a new PIN failed, in words for the player. Never says how long a lockout lasts. */
export function describeChangePinError(code: string, serverMessage: string): string {
  switch (code) {
    case 'wrong_pin':
      return 'That current PIN is not right.';
    case 'locked':
      return 'Too many wrong attempts. Ask the staff for help.';
    case 'unknown':
      return 'No player with that nickname is registered.';
    case 'disabled':
      return 'Leaderboard is not available currently.';
    case 'reset_expired':
      return 'The PIN reset has expired. Ask the staff to unlock your account again.';
    default:
      return serverMessage || 'Could not change the PIN.';
  }
}

/**
 * Chooses a new PIN. The current one (or the temporary one staff gave) is checked first, exactly like starting a game, so
 * nobody can take over an account by guessing. Nothing here stores either PIN.
 */
export async function changeLeaderboardPin(
  nickname: string,
  pin: string,
  newPin: string,
): Promise<{ ok: true; nickname: string } | { ok: false; error: string }> {
  const res = await relayRequest('POST', '/leaderboard/change-pin', { nickname, pin, newPin });
  if (isFailure(res)) return { ok: false, error: res.error };
  if (!res.ok) {
    const code = typeof res.json.code === 'string' ? res.json.code : '';
    return { ok: false, error: describeChangePinError(code, String(res.json.error ?? '')) };
  }
  return { ok: true, nickname: String(res.json.nickname ?? nickname) };
}

// --- turning a period into a game ---------------------------------------------

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The house rules a leaderboard game will be played under, read from the period's frozen rules.
 * The relay never looks inside them (rule 1), so this is the only place they are checked: they must
 * name a mode, that mode must match the period's declared settlement style (zero-sum = Serious,
 * winner-only = Fun — CLAUDE.md rule 3), and the settings must be the same shape a saved game's are.
 * Missing values fall back to the app's defaults, exactly as for a normal game.
 */
export function periodRulesToSettings(
  period: Pick<LeaderboardPeriodInfo, 'rules' | 'settlement'>,
): { ok: true; settings: HkosSettings } | { ok: false; error: string } {
  const bad = (why: string) => ({ ok: false as const, error: `This leaderboard's rules could not be read (${why}). Ask staff to check it.` });
  const rules = period.rules;
  if (!isPlainObject(rules)) return bad('no rules');
  const mode = rules.mode;
  if (mode !== 'serious' && mode !== 'fun') return bad('unknown scoring mode');
  const expected = SETTLEMENT_MODE[period.settlement];
  if (expected === undefined) return bad('unknown scoring style');
  if (mode !== expected) return bad('the rules and the scoring style disagree');

  const overrides: Record<string, unknown> = { mode };
  for (const key of ['toggles', 'serious', 'fun'] as const) {
    if (rules[key] === undefined) continue;
    if (!isPlainObject(rules[key])) return bad(`${key} is not readable`);
    overrides[key] = rules[key];
  }
  const settings = makeSettings(overrides as Parameters<typeof makeSettings>[0]);
  if (!settingsLookRight(settings, DEFAULT_SETTINGS)) return bad('a setting has the wrong kind of value');
  // The Hand helper is advice during play: never in a leaderboard game, whatever the period's rules happen to carry.
  settings.toggles = { ...settings.toggles, handHelper: false };
  return { ok: true, settings };
}

/** The record kept on the game so step 4 can submit its result. */
export function leaderboardInfoFrom(started: StartedLeaderboardGame, now: number): LeaderboardInfo {
  return {
    gameId: started.gameId,
    periodId: started.period.id,
    venue: started.period.venue,
    settlement: started.period.settlement,
    notCounted: started.notCounted,
    startedAt: now,
  };
}
